import {FC} from 'react'
import {Link} from 'react-router-dom'
import {
  formatInvoiceAmount,
  formatInvoiceCount,
} from '../../../modules/apps/invoice-management/invoices-list/core/_formatters'
import {readDashboardError} from './core/_requests'
import {
  formatAge,
  getProductListUrl,
  getProductQuantityText,
} from './core/dashboardActivityPresentation'
import {useDashboardActivity} from './core/useDashboardData'

type Props = {
  companyId: number
}

const DashboardRecentProducts: FC<Props> = ({companyId}) => {
  const {data, isLoading, isError, error, refetch} = useDashboardActivity(companyId)
  const products = data?.products || []

  return (
    <section
      className='mp-panel mp-products'
      aria-labelledby='mp-products-title'
      aria-busy={isLoading}
    >
      <div className='mp-panel__head'>
        <div>
          <span>کالا و خدمات</span>
          <h2 id='mp-products-title'>آخرین کالا و خدمات صورتحساب‌شده</h2>
          {products.length > 0 && (
            <small className='mp-panel__note'>
              بر اساس {formatInvoiceCount(data?.productInvoiceWindow || 0)} صورتحساب اخیر
            </small>
          )}
        </div>
        <Link
          className='btn btn-icon btn-light mp-panel__refresh'
          to='/apps/product-management/products'
          aria-label='مدیریت کالا و خدمات'
          title='مدیریت کالا و خدمات'
        >
          <i className='las la-dolly' aria-hidden='true' />
        </Link>
      </div>

      {isLoading ? (
        <div className='mp-errors__loading' aria-hidden='true'>
          <span />
          <span />
        </div>
      ) : isError && !data ? (
        <div className='mp-dashboard-alert' role='alert'>
          <i className='las la-exclamation-circle' aria-hidden='true' />
          <span>{readDashboardError(error, 'فهرست کالا و خدمات اخیر دریافت نشد.')}</span>
          <button type='button' className='btn btn-sm btn-light-primary' onClick={() => refetch()}>
            تلاش مجدد
          </button>
        </div>
      ) : products.length === 0 ? (
        <div className='mp-dashboard-empty mp-dashboard-empty--wide'>
          هنوز کالا یا خدمتی در صورتحساب‌های اخیر ثبت نشده است.
        </div>
      ) : (
        <ul className='mp-products__list'>
          {products.map((product) => {
            const quantity = getProductQuantityText(product)
            const [codeLabel, codeValue] = product.stuffId
              ? ['شناسه', product.stuffId]
              : product.internalCode
              ? ['کد داخلی', product.internalCode]
              : ['بدون شناسه کالا', '']

            return (
              <li key={product.productServiceId}>
                <Link
                  className='mp-products__item'
                  to={getProductListUrl(product)}
                  title='مشاهده صورتحساب‌های این کالا یا خدمت در فهرست'
                >
                  <span
                    className={`mp-products__type ${product.type === 1 ? 'is-good' : 'is-service'}`}
                  >
                    {product.type === 1 ? 'کالا' : 'خدمت'}
                  </span>
                  <span className='mp-products__name'>
                    <strong>{product.name || 'بدون نام'}</strong>
                    {/* Isolated runs: two numbers side by side would otherwise swap places in RTL.
                        A Persian comma between them: beside Persian digits «·» reads as «۰». */}
                    <small>
                      {codeLabel}
                      {codeValue && (
                        <>
                          {' '}
                          <bdi>{codeValue}</bdi>
                        </>
                      )}
                      {quantity && (
                        <>
                          {'، '}
                          <bdi>{quantity}</bdi>
                        </>
                      )}
                    </small>
                  </span>
                  {/* Two lines, like the name beside it: the row stays as short as its text. */}
                  <span className='mp-products__stats'>
                    <strong>{formatInvoiceCount(product.invoices)} صورتحساب</strong>
                    <small>
                      <bdi>
                        {formatInvoiceAmount(product.netAmountExact ?? product.netAmount)} ریال
                      </bdi>
                      {'، '}
                      {formatAge(product.ageSeconds)}
                    </small>
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

export {DashboardRecentProducts}
